/**
 * **主题文档的类型契约** —— 纯类型，零运行时。
 *
 * ## 为什么这些类型住在 `types/` 而不是 `theme/`
 *
 * 主题要能管**布局**（`RegionPlacement`），而布局层也要读它。
 * 但 `scripts/verify-layers.ts` 钉死的依赖方向是：
 *
 * ```text
 *   theme  → 只许依赖 types / theme
 *   layout → 只许依赖 types / layout
 * ```
 *
 * 两者**互相看不见**。所以共用的数据形状只能沉到 `types/` ——
 * 这不是为了好看，是因为放在任何一边，另一边都拿不到（静态守卫会红）。
 *
 * ## 主题是什么（一句话）
 *
 * **主题是数据，不是代码。** 它描述「要什么」，不描述「怎么画」：
 *
 * ```text
 *   ThemeDocument
 *   ├── palette   语义色（强调色 / 分类 / 端口 / 严重级）→ 引擎派生成一组令牌
 *   ├── tokens    原样写入的 CSS 变量          ┐
 *   ├── recipes   命名变体 → data-np-* 属性     ├─ 通道 ①②：结构化、可校验
 *   ├── css       任意 CSS（作用域化注入）      ┘  通道 ③：逃生舱，不受限
 *   └── layout    区域放置 / 密度               ── 通道 ⑤：非 CSS 的那一半
 * ```
 *
 * 通道 ④（JS 读回）没有对应的文档字段 —— 它是**运行时机制**：
 * 任何需要颜色/尺寸的 JS（连线描边、小地图、SVG）都从 DOM 反读
 * 解析后的 CSS 变量，于是主题作者**不需要知道 JS 的存在**。
 *
 * ## ⚠️ 键名约定
 *
 * `tokens` 的键可以写 `--np-accent`，也可以写 `accent`（引擎补前缀）。
 * 两种都收，因为主题作者手写 JSON 时很容易漏掉前缀，
 * 而**漏掉前缀的后果是静默失效** —— 这正是这个仓库最忌讳的一类错。
 */

/* ══════════════════════════════════════════════════════════════════
   区域放置 —— 「让工具栏浮动」这件事的数据形状
   ══════════════════════════════════════════════════════════════════ */

/** 停靠边。语义同 CSS 方位，但**不是** CSS 属性名 */
export type RegionDockEdge = 'top' | 'bottom' | 'left' | 'right'

/** 浮层的锚角。`tl` = 左上，`br` = 右下 */
export type RegionCorner = 'tl' | 'tr' | 'bl' | 'br'

/**
 * 一个区域（Region）怎么摆。
 *
 * ⚠️ **为什么不是一个 boolean**：`RegionState` 原来只有开/关，
 * 「工具栏能不能浮动」这件事在数据里**无处安放** —— 位置是模板写死的
 * （`NpArea.vue` 里 toolbar 固定在 main 之前）。主题要能改它，
 * 就得先把它变成数据。
 *
 * ⚠️ **`reserveSpace` 是这里最重要的一个字段**，别删：
 * `NpArea.vue` 的头部注释记着上一轮重构的核心收益 ——
 * 「Region 是**布局盒子**，不是浮层」，因为浮层会让 `fitView()`
 * 把节点塞进面板底下（真机事故）。
 *
 *   · `reserveSpace: true`（默认）—— 视觉上浮起（留白 / 圆角 / 阴影），
 *     但**仍然占位**，画布拿到的还是真实剩余空间。零风险。
 *   · `reserveSpace: false` —— 真覆盖画布。这时候区域**必须**把
 *     自己的矩形上报给画布（`--np-region-inset-*`），否则就是重演
 *     当年「节点被塞到面板底下」那个 bug。
 */
export interface RegionPlacement {
  /** 显示还是隐藏。`false` 等于整个区域不存在（不渲染、不占位） */
  visible?: boolean
  /** `dock` = 贴边占位；`float` = 浮起。默认 `dock` */
  mode?: 'dock' | 'float'
  /** `dock` 时贴哪条边 */
  edge?: RegionDockEdge
  /** `float` 时锚在哪个角 */
  corner?: RegionCorner
  /** 厚度 / 宽度（px）。`'auto'` = 由内容决定。不写就用该区域的内置默认 */
  size?: number | 'auto'
  /** `float` 时离边的距离（px） */
  gap?: number
  /** `float` 时的圆角（px） */
  radius?: number
  /** 排布方向。工具栏默认 `column`（竖排一条） */
  orientation?: 'row' | 'column'
  /** 同一容器内多个区域的先后顺序，小的在前 */
  order?: number
  /** 是否可折叠（折叠按钮由区域自己渲染；主题只声明能力） */
  collapsible?: boolean
  /**
   * 浮起时是否**仍然占位**。
   *
   * ⚠️ 默认 `true`。改成 `false` 之前请先读上面那段关于 `fitView` 的说明。
   */
  reserveSpace?: boolean
  /** 浮层不透明度 0..1 */
  opacity?: number
}

/* ══════════════════════════════════════════════════════════════════
   配方槽 —— 「一个词换一套观感」的词汇表
   ══════════════════════════════════════════════════════════════════ */

/**
 * 可换配方的部件。
 *
 * ⚠️ **这些名字不是枚举，是「槽位」。** 每个槽位有一组**内置**取值
 * （见 `theme/recipeCatalog.ts`），但主题包可以自带 CSS 定义新取值 ——
 * 内核只负责把名字写成 `data-np-<slot>="<name>"` 属性，怎么画由 CSS 说。
 * 这样既是「开箱可选」，又不封顶。
 */
export type RecipeSlot =
  /** 整体外壳 / 窗口底色 */
  | 'shell'
  /** 节点壳：填充方式（纯色 / 渐变 / 玻璃 / 描边 / 霓虹…） */
  | 'node'
  /** 节点头：底色与装饰（纯色 / 渐变 / 左强调条 / 下划线 / 极简） */
  | 'header'
  /** 节点行：分隔方式（平铺 / 斑马纹 / 分隔线 / 宽松） */
  | 'row'
  /** 内联控件：输入框观感 */
  | 'widget'
  /** 端口形状（圆 / 方 / 菱形 / 环） */
  | 'socket'
  /** 选中与活动态的画法（描边环 / 辉光 / 阴影 / 加粗） */
  | 'state'
  /** 菜单与弹出层 */
  | 'popup'

/** 已选配方。没写的槽位用内置默认 */
export type RecipeSelection = Partial<Record<RecipeSlot, string>>

/* ══════════════════════════════════════════════════════════════════
   布局
   ══════════════════════════════════════════════════════════════════ */

/**
 * 密度。
 *
 * 这是**性价比最高的一根轴**：一处声明就能同时缩放控件高度、行高、
 * 内边距、字号 —— 而它影响的是「这个工具看起来紧不紧凑」。
 */
export type ThemeDensity = 'compact' | 'normal' | 'comfortable'

/**
 * 全局外壳（在所有 Area 之外的那几条）。
 *
 * ⚠️ **只有顶栏**。底部那条快捷键提示条是**每个 Area 自己的 Region**
 * （`regions[<编辑器>].statusbar`），不是全局条 —— 所以它由
 * `ThemeLayout.regions` 管，不在这里重复一个字段。
 *
 * 这里曾经有一个 `statusbar`，但没有任何元素对应它。**一个声明了却
 * 没人实现的字段比没有字段更坏**：主题作者会去设它，然后怀疑自己写错了。
 */
export interface ThemeChromeLayout {
  /** 顶部菜单栏 / 品牌栏 */
  header?: RegionPlacement
}

/**
 * 画布覆盖层的摆放（小地图等 —— 它们是 Area 内部覆盖层，不是 Region）。
 *
 * ⚠️ 这里曾经还有一个 `fitViewPadding`，但 `fitView()` 的 padding 是
 * **逐调用点**决定的（换层 0.35、加节点定位 0.3、用户按快捷键 0.2），
 * 一个全局值表达不了这个差异。与其做一个只对其中一个调用点生效的
 * 半吊子设置，不如把字段去掉 —— 想改就等真的需要「全局统一」时再说。
 */
export interface ThemeCanvasLayout {
  /** 小地图贴哪个角 */
  minimapCorner?: RegionCorner
  minimapWidth?: number
  minimapHeight?: number
}

export interface ThemeSplitLayout {
  /**
   * 分割条的**可见**厚度（px）。命中区由 `NpSplitHandle` 自己撑开，
   * 所以调窄不会让鼠标更难抓；触摸端另有下限（见 `NpAreaTree.vue`）。
   */
  size?: number
}

/**
 * 主题能决定的**结构与几何**。
 *
 * ⚠️ `regions` 用 `string` 做键（而不是 `EditorType` / `RegionKey`），
 * 是**为了分层**：`theme/` 不许 import `layout/`（见文件头）。
 * 代价是这里没有编译期校验，所以校验挪到了运行时 ——
 * `theme/validate.ts` 的 `validateLayoutKeys()` 会拿真实的编辑器表去对，
 * 由 `verify:theme` 守着。**宁可要一个会报错的运行时校验，
 * 也不要一个为了类型好看而破掉的分层。**
 */
/**
 * **动效强度**（通道 ⑥）。
 *
 * ⚠️ 做成主题的一个字段，而不是散在各个组件里写死 `transition: 150ms`。
 * 三个理由：
 *   · 用户要能**一键关掉**（有人就是嫌动效烦，有人是晕动症）；
 *   · 它是"这个主题什么手感"的一部分，和密度同类；
 *   · 写死的话，同一个动效会在十几个组件里各写一遍，时长迟早漂。
 *
 * ⚠️ 表达成 `data-np-motion` 属性 + `--np-motion-scale` **系数**，
 * 不是直接产出时长令牌 —— 理由和密度那次一样：
 * 主题的变量是**内联**写在 `<html>` 上的，内联赢过样式表，
 * 那样「系统开了『减少动态效果』就全部关掉」那条媒体查询会被静默架空。
 * 系数 + 属性之后，媒体查询永远说了算。
 */
export type ThemeMotion =
  /** 完全不动（晕动症 / 追求极致响应） */
  | 'off'
  /** 只保留必要的位移动效，时长压到 ~65% */
  | 'subtle'
  /** 默认 */
  | 'normal'

export interface ThemeLayout {
  density?: ThemeDensity
  motion?: ThemeMotion
  chrome?: ThemeChromeLayout
  /** `{ [editorId]: { [regionKey]: RegionPlacement } }` */
  regions?: Record<string, Record<string, RegionPlacement>>
  split?: ThemeSplitLayout
  canvas?: ThemeCanvasLayout
}

/* ══════════════════════════════════════════════════════════════════
   调色板
   ══════════════════════════════════════════════════════════════════ */

export interface ThemeSeverityPalette {
  error?: string
  warning?: string
  info?: string
  success?: string
}

/**
 * 语义色。**这是主题作者主要该写的东西。**
 *
 * 派生规则（`theme/derive.ts`）：
 *
 * ```text
 *   accent            → --np-accent，并派生出 --np-accent-soft（12% 透明）
 *                       与 --np-accent-solid
 *   categories.<id>   → --np-cat-<id>
 *   sockets.<type>    → --np-socket-<type>
 *   severity.*        → --np-severity-*
 * ```
 *
 * ⚠️ **没声明的分类不会掉进「同一个灰」**：现在的实现是未知分类一律
 * `utility` 的 `#454545`，于是新增一个包，它所有节点头都变成同一个颜色。
 * 改成按 id 做**确定性色相旋转**：同 id 永远同色、不同 id 大概率不同色。
 */
export interface ThemePalette {
  accent?: string
  danger?: string
  /** 分类色。键是包声明的分类 id（`data` / `logic` / `text`…） */
  categories?: Record<string, string>
  /** 端口色。键是 `SocketType` */
  sockets?: Record<string, string>
  severity?: ThemeSeverityPalette
}

/* ══════════════════════════════════════════════════════════════════
   主题文档
   ══════════════════════════════════════════════════════════════════ */

/** 当前的文档格式版本。改动不兼容结构时 +1，并在 `themeDoc.ts` 里写迁移 */
export const THEME_FORMAT_VERSION = 1

export interface ThemeDocument {
  /** 格式版本。缺省按 1 处理 */
  version?: number
  id: string
  name: string
  /** 一句话说明这份主题适合什么场景（菜单里当副标题） */
  hint?: string
  /**
   * 继承另一个主题的 id。
   *
   * ⚠️ **解析顺序是「先祖先、后自己」**，所以子主题只需要写**差异**。
   * 这条链由 `resolveTheme()` 展开，环会被检测出来并报错（不是静默截断）。
   */
  extends?: string
  /**
   * 底色是深还是浅。
   *
   * ⚠️ 它不只影响观感 —— 它决定 `color-scheme`：
   * 不声明的话，Android Chrome 的「强制深色网页」会把页面反暗，
   * **包括我们已经画成浅色的部分**（真机事故，见 `theme.css` 的注释）。
   * 所以浅色主题必须是 `only light`。
   */
  scheme?: 'dark' | 'light'
  palette?: ThemePalette
  /** 原样写入的 CSS 变量。键可带 `--np-` 前缀，也可以不带 */
  tokens?: Record<string, string>
  recipes?: RecipeSelection
  /**
   * **逃生舱**：任意 CSS，注入时按主题作用域变换。
   *
   * 这是「基本不受限制」那一半。作用域是 `html[data-np-theme="<id>"]`，
   * 变换规则见 `theme/scopeCss.ts`。想完全不管作用域就写 `@global`。
   */
  css?: string
  layout?: ThemeLayout
}

/** 一份**已解析**的主题：继承链展开、调色板派生成令牌、CSS 已合并 */
export interface ResolvedTheme {
  /** 展开后的完整文档（`extends` 已消解） */
  doc: ThemeDocument
  id: string
  name: string
  hint?: string
  scheme: 'dark' | 'light'
  /** 已补 `--np-` 前缀的最终令牌表 */
  tokens: Record<string, string>
  recipes: RecipeSelection
  /** 继承链上所有文档的 `css` 按序拼接（祖先在前） */
  css: string
  layout: ThemeLayout
  /** 从根到自己的 id 链，UI 展示与排错用 */
  chain: string[]
}

/** 主题在菜单/画廊里展示用的摘要（不需要解析就能拿到） */
export interface ThemeSummary {
  id: string
  name: string
  hint?: string
  scheme: 'dark' | 'light'
  /** 是不是内置主题（内置的删不掉） */
  builtin: boolean
  /** 继承自谁 */
  extends?: string
}
